import React, {useRef, useState} from 'react';
import {analysisApi, classificationsApi} from '../services/api';
import {AIProvider, AIProviderInfo, AnalysisResult, BrandSafetyPrompt, BulkAnalysisResult} from '../types';
import AnalysisResultsTable from './AnalysisResultsTable';
import {exportAllResults} from '../utils/exportUtils';
import {MAX_DOMAIN_FILTER, parseDomainFilter} from '../utils/domainFilter';
import {RelevanceEngineConfig} from './relevance-engine/useRelevanceEngineConfig';
import CountryMultiSelect from './CountryMultiSelect';
import {Button} from './ui/button';

interface RelevanceEngineABProps {
    config: RelevanceEngineConfig;
}

const RelevanceEngineAB: React.FC<RelevanceEngineABProps> = ({config}) => {
    const {selectedPromptId, selectedProvider, selectedModel, maxUrlsNonAdmin, isAdmin} = config;

    const [urlsText, setUrlsText] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [result, setResult] = useState<BulkAnalysisResult | null>(null);
    const [partialResults, setPartialResults] = useState<AnalysisResult[]>([]);
    const partialResultsRef = useRef<AnalysisResult[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [excludedUrlsCount, setExcludedUrlsCount] = useState<number | null>(null);
    const [originalRequestedCount, setOriginalRequestedCount] = useState<number | null>(null);
    const [progress, setProgress] = useState({
        current: 0, total: 0, currentChunk: 0, totalChunks: 0, status: '',
        currentAction: 0, totalActions: 0, actionType: '' as 'fetching' | 'analyzing' | ''
    });
    const [streamingAbort, setStreamingAbort] = useState<(() => void) | null>(null);
    const [showExportMenu, setShowExportMenu] = useState(false);

    // Classification-based URL loading
    const [databaseUrlCount, setDatabaseUrlCount] = useState(100);
    const [isLoadingUrls, setIsLoadingUrls] = useState(false);
    const [domainFilter, setDomainFilter] = useState('');
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [similaritySelectivity, setSimilaritySelectivity] = useState(0);
    // Country-distribution mode for multi-country classification fetches
    // (bd-g6m3q.3). Defaults to the backend default ('uniform', bd-yy3mv).
    const [countryDistributionMode, setCountryDistributionMode] = useState<'uniform' | 'proportional'>('uniform');
    // Exclude homepages from the classification URL fetch (bd-deepcontext-vql07).
    // Default CHECKED (true), matching the scheduled-jobs control.
    const [excludeHomepages, setExcludeHomepages] = useState(true);

    // Manual input state
    const [inputMode, setInputMode] = useState<'manual' | 'classifications'>('manual');
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

    const loadClassificationUrls = async () => {
        setIsLoadingUrls(true);
        setError(null);
        try {
            // Preview must apply exactly the filters the analysis applies, so this
            // payload mirrors the request handleAnalyze builds below — countries,
            // country distribution, homepage exclusion, similarity selectivity and
            // the full domain list (bd deepcontext-4zil).
            const parsedDomains = parseDomainFilter(domainFilter);
            const data = await classificationsApi.getDatabaseUrls({
                promptId: selectedPromptId || undefined as any,
                limit: databaseUrlCount,
                classificationDomains: parsedDomains.length > 0 ? parsedDomains : undefined,
                classificationCountry: selectedCountries.length > 0 ? selectedCountries : undefined,
                classificationSimilaritySelectivity: similaritySelectivity > 0 ? similaritySelectivity : undefined,
                countryDistributionMode,
                excludeHomepages,
            });
            setUrlsText(data.urls.join('\n'));
        } catch (err: any) {
            const apiError = err.response?.data?.error;
            setError(typeof apiError === 'string' ? apiError : apiError?.message || err.message || 'Failed to preview URLs from classifications');
        } finally {
            setIsLoadingUrls(false);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setUrlsText(content.split(/[\n,]/).map(u => u.trim()).filter(u => u).join('\n'));
            setUploadedFileName(file.name);
        };
        reader.readAsText(file);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (!file || !(file.type === 'text/plain' || file.type === 'text/csv' || file.name.endsWith('.txt') || file.name.endsWith('.csv'))) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result as string;
            setUrlsText(content.split(/[\n,]/).map(u => u.trim()).filter(u => u).join('\n'));
            setUploadedFileName(file.name);
        };
        reader.readAsText(file);
    };

    const switchMode = (mode: 'manual' | 'classifications') => {
        setInputMode(mode);
        setUrlsText('');
        setUploadedFileName(null);
        setResult(null);
        setPartialResults([]);
    };

    const handleAnalyze = async () => {
        const urls = urlsText.split('\n').filter(line => line.trim());
        if (inputMode === 'manual' && urls.length === 0) { setError('Please enter at least one valid URL'); return; }

        const maxUrls = isAdmin ? 10000 : maxUrlsNonAdmin;
        if (inputMode === 'manual' && urls.length > maxUrls) {
            setError(`Maximum ${maxUrls} URLs allowed per batch`);
            return;
        }

        const isFromClassifications = inputMode === 'classifications';

        // Parse + cap-check the domain filter for classification mode. Mirrors
        // the validation plain RE (ContextAnalyzer) performs in the same flow.
        let parsedDomains: string[] = [];
        if (isFromClassifications) {
            parsedDomains = parseDomainFilter(domainFilter);
            if (parsedDomains.length > MAX_DOMAIN_FILTER) {
                setError(`Maximum ${MAX_DOMAIN_FILTER} domains allowed. You have ${parsedDomains.length} domains.`);
                return;
            }
        }

        const estimatedTotal = isFromClassifications ? databaseUrlCount : urls.length;

        resetAnalysisState();
        setOriginalRequestedCount(estimatedTotal);
        setProgress({
            current: 0, total: estimatedTotal, currentChunk: 0,
            totalChunks: Math.ceil(estimatedTotal / 10), status: 'Starting analysis...',
            currentAction: 0, totalActions: estimatedTotal * 2, actionType: 'fetching'
        });

        const request: any = {
            urls, promptId: selectedPromptId, provider: selectedProvider, model: selectedModel,
        };
        if (isFromClassifications) {
            request.analyzeFromClassifications = true;
            request.classificationUrlCount = databaseUrlCount;
            request.classificationDomains = parsedDomains.length > 0 ? parsedDomains : undefined;
            request.classificationCountry = selectedCountries.length > 0 ? selectedCountries : undefined;
            request.classificationSimilaritySelectivity = similaritySelectivity > 0 ? similaritySelectivity : undefined;
            // Only meaningful with >1 country selected; always send so the
            // backend honors the choice (bd-g6m3q.3).
            request.countryDistributionMode = countryDistributionMode;
            // Homepage exclusion (bd-deepcontext-vql07) — mirrors scheduled jobs.
            request.excludeHomepages = excludeHomepages;
        }

        try {
            const {abort} = await analysisApi.analyzeStreaming(request, buildStreamingCallbacks());
            setStreamingAbort(() => abort);
        } catch (err: any) {
            handleAnalysisError(err);
        }
    };

    const handleCancelAnalysis = () => {
        if (streamingAbort) {
            streamingAbort();
            setIsAnalyzing(false);
            setError('Analysis cancelled by user');
        }
    };

    const displayResults = partialResults.length > 0 ? partialResults : (result?.results || []);

    return (
        <div>
            <UrlInputSection
                inputMode={inputMode}
                onSwitchMode={switchMode}
                urlsText={urlsText}
                onUrlsTextChange={setUrlsText}
                isDragOver={isDragOver}
                onDragOver={setIsDragOver}
                onDrop={handleDrop}
                onFileUpload={handleFileUpload}
                uploadedFileName={uploadedFileName}
                databaseUrlCount={databaseUrlCount}
                onDatabaseUrlCountChange={setDatabaseUrlCount}
                domainFilter={domainFilter}
                onDomainFilterChange={setDomainFilter}
                selectedCountries={selectedCountries}
                onCountriesChange={setSelectedCountries}
                similaritySelectivity={similaritySelectivity}
                onSimilaritySelectivityChange={setSimilaritySelectivity}
                countryDistributionMode={countryDistributionMode}
                onCountryDistributionModeChange={setCountryDistributionMode}
                excludeHomepages={excludeHomepages}
                onExcludeHomepagesChange={setExcludeHomepages}
                onLoadUrls={loadClassificationUrls}
                isLoadingUrls={isLoadingUrls}
                selectedPromptId={selectedPromptId}
                isAdmin={isAdmin}
                isAnalyzing={isAnalyzing}
                onAnalyze={handleAnalyze}
                onCancel={handleCancelAnalysis}
                streamingAbort={streamingAbort}
            />

            {isAnalyzing && <ProgressDisplay progress={progress} originalRequestedCount={originalRequestedCount} />}
            {warning && <WarningDisplay message={warning} />}
            {error && <ErrorDisplay message={error} />}

            {(result || partialResults.length > 0) && (
                <div className="mt-8 space-y-6">
                    {result && (
                        <SummarySection
                            result={result}
                            excludedUrlsCount={excludedUrlsCount}
                            isAdmin={isAdmin}
                        />
                    )}
                    {displayResults.length > 0 && (
                        <ResultsSection
                            displayResults={displayResults}
                            showExportMenu={showExportMenu}
                            onToggleExportMenu={() => setShowExportMenu(!showExportMenu)}
                            result={result}
                            onFeedbackSubmitted={(id, agree) => {
                                setPartialResults(prev => prev.map(r =>
                                    r.id === id ? {...r, feedback: {agree, timestamp: new Date().toISOString()}} : r
                                ));
                                if (result) {
                                    setResult({
                                        ...result,
                                        results: result.results.map(r =>
                                            r.id === id ? {...r, feedback: {agree, timestamp: new Date().toISOString()}} : r
                                        )
                                    });
                                }
                            }}
                        />
                    )}
                </div>
            )}
        </div>
    );

    function resetAnalysisState() {
        setIsAnalyzing(true);
        setError(null);
        setWarning(null);
        setExcludedUrlsCount(null);
        setResult(null);
        setPartialResults([]);
        partialResultsRef.current = [];
        setShowExportMenu(false);
    }

    function emptyProgress() {
        return {current: 0, total: 0, currentChunk: 0, totalChunks: 0, status: '', currentAction: 0, totalActions: 0, actionType: '' as ''};
    }

    function handleAnalysisError(err: any) {
        if (err.name === 'AbortError') setError('Analysis was cancelled');
        else setError(err.message || 'Failed to analyze URLs');
        setIsAnalyzing(false);
        setStreamingAbort(null);
        setProgress(emptyProgress());
    }

    function buildStreamingCallbacks() {
        return {
            onProgress: (data: any) => {
                setProgress(prev => {
                    if (data.chunksCompleted !== undefined && data.totalChunks !== undefined) {
                        const chunkSize = data.total ? Math.ceil(data.total / data.totalChunks) : 20;
                        const totalUrls = data.total ?? (data.totalChunks * chunkSize || prev.total);
                        const urlsProcessed = data.current ?? (data.chunksCompleted * chunkSize);
                        return {
                            ...prev, current: urlsProcessed, total: totalUrls,
                            currentChunk: data.chunksCompleted, totalChunks: data.totalChunks,
                            totalActions: totalUrls * 2, currentAction: totalUrls + urlsProcessed,
                            status: data.message || `Processing chunks: ${data.chunksCompleted}/${data.totalChunks}`,
                            actionType: 'analyzing'
                        };
                    }
                    if (data.status === 'queued' && data.total !== undefined) {
                        return {
                            ...prev, total: data.total, totalActions: data.total * 2,
                            totalChunks: data.totalChunks ?? prev.totalChunks,
                            status: data.message || data.status || prev.status, currentAction: 0, actionType: 'fetching'
                        };
                    }
                    let actionsCompleted = prev.currentAction;
                    let actionType = prev.actionType;
                    if (data.status?.includes('Loaded') && data.status?.includes('URLs')) {
                        actionsCompleted = data.total;
                        actionType = 'analyzing';
                    } else if (data.status?.includes('Starting analysis')) {
                        actionsCompleted = data.total;
                        actionType = 'analyzing';
                    }
                    return {
                        ...prev,
                        total: data.total ?? prev.total, current: data.current ?? prev.current,
                        totalChunks: data.totalChunks ?? prev.totalChunks, currentChunk: data.currentChunk ?? prev.currentChunk,
                        totalActions: (data.total ?? prev.total) * 2,
                        status: data.status || data.message || prev.status,
                        currentAction: actionsCompleted, actionType
                    };
                });
            },
            onChunkStart: (data: any) => {
                setProgress(prev => ({
                    ...prev, currentChunk: data.chunkIndex, totalChunks: data.totalChunks,
                    status: `Analyzing chunk ${data.chunkIndex}/${data.totalChunks} (${data.urlsInChunk} URLs)...`,
                    actionType: 'analyzing' as 'analyzing'
                }));
            },
            onWarning: (data: any) => {
                if (data.message) {
                    setWarning(data.message);
                    if (data.urlsRequested !== undefined && data.urlsFound !== undefined) {
                        const excluded = data.urlsRequested - data.urlsFound;
                        setExcludedUrlsCount(excluded > 0 ? excluded : null);
                    }
                    setProgress(prev => ({...prev, status: data.message || prev.status, total: data.urlsFound ?? prev.total}));
                }
            },
            onChunkComplete: (data: any) => {
                const newResults = data.results || [];
                partialResultsRef.current = [...partialResultsRef.current, ...newResults];
                setPartialResults([...partialResultsRef.current]);
                const totalUrlsCompleted = partialResultsRef.current.length;
                setProgress(prev => ({
                    ...prev, current: totalUrlsCompleted,
                    currentAction: prev.total + totalUrlsCompleted,
                    currentChunk: data.chunkIndex || prev.currentChunk,
                    status: totalUrlsCompleted < prev.total ? `Analyzed ${totalUrlsCompleted}/${prev.total} URLs` : 'Finalizing results...'
                }));
            },
            onComplete: (data: any) => {
                const finalResults = partialResultsRef.current;
                const s = data.summary || {};
                setResult({
                    id: data.bulkResultId || '',
                    promptId: selectedPromptId,
                    results: finalResults,
                    totalCost: data.totalCost || 0,
                    analyzedAt: data.analyzedAt || new Date().toISOString(),
                    summary: {
                        totalUrls: s.totalUrls ?? finalResults.length,
                        matchedUrls: s.matchedUrls ?? finalResults.filter((r: AnalysisResult) => r.isBrandSafe).length,
                        unmatchedUrls: s.unmatchedUrls ?? finalResults.filter((r: AnalysisResult) => !r.isBrandSafe).length,
                        failedUrls: s.failedUrls ?? 0,
                        averageConfidence: s.averageConfidence ?? (finalResults.reduce((sum: number, r: AnalysisResult) => sum + r.confidence, 0) / finalResults.length || 0),
                        totalInputTokens: s.totalInputTokens ?? finalResults.reduce((sum: number, r: AnalysisResult) => sum + (r.inputTokens || 0), 0),
                        totalOutputTokens: s.totalOutputTokens ?? finalResults.reduce((sum: number, r: AnalysisResult) => sum + (r.outputTokens || 0), 0),
                    }
                });
                setPartialResults([]);
                partialResultsRef.current = [];
                setIsAnalyzing(false);
                setStreamingAbort(null);
                setProgress(emptyProgress());
            },
            onError: (errorMsg: string) => {
                setError(errorMsg);
                setIsAnalyzing(false);
                setStreamingAbort(null);
                setProgress(emptyProgress());
            }
        };
    }
};

/* ──────────────────── URL Input Section ──────────────────── */

interface UrlInputSectionProps {
    inputMode: 'manual' | 'classifications';
    onSwitchMode: (m: 'manual' | 'classifications') => void;
    urlsText: string;
    onUrlsTextChange: (v: string) => void;
    isDragOver: boolean;
    onDragOver: (v: boolean) => void;
    onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
    onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    uploadedFileName: string | null;
    databaseUrlCount: number;
    onDatabaseUrlCountChange: (v: number) => void;
    domainFilter: string;
    onDomainFilterChange: (v: string) => void;
    selectedCountries: string[];
    onCountriesChange: (v: string[]) => void;
    similaritySelectivity: number;
    onSimilaritySelectivityChange: (v: number) => void;
    countryDistributionMode: 'uniform' | 'proportional';
    onCountryDistributionModeChange: (v: 'uniform' | 'proportional') => void;
    excludeHomepages: boolean;
    onExcludeHomepagesChange: (v: boolean) => void;
    onLoadUrls: () => void;
    isLoadingUrls: boolean;
    selectedPromptId: string;
    isAdmin: boolean;
    isAnalyzing: boolean;
    onAnalyze: () => void;
    onCancel: () => void;
    streamingAbort: (() => void) | null;
}

function UrlInputSection(props: UrlInputSectionProps) {
    return (
        <div>
            <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-ink">Input Mode:</span>
                    <div className="inline-flex rounded-full bg-ghost-bg p-[3px]">
                        <Button
                            type="button"
                            variant="pill"
                            active={props.inputMode === 'manual'}
                            onClick={() => props.onSwitchMode('manual')}
                        >
                            Manual Input
                        </Button>
                        <Button
                            type="button"
                            variant="pill"
                            active={props.inputMode === 'classifications'}
                            onClick={() => props.onSwitchMode('classifications')}
                        >
                            From Classifications
                        </Button>
                    </div>
                </div>
                <p className="text-sm text-ink-muted mt-2">
                    {props.inputMode === 'manual'
                        ? 'Enter URLs manually or upload a file'
                        : 'Load pre-classified URLs — optionally filtered by blueprint categories for better matching'
                    }
                </p>
            </div>

            <div className="space-y-4">
                {props.inputMode === 'classifications' && (
                    <ClassificationsControls
                        databaseUrlCount={props.databaseUrlCount}
                        onDatabaseUrlCountChange={props.onDatabaseUrlCountChange}
                        domainFilter={props.domainFilter}
                        onDomainFilterChange={props.onDomainFilterChange}
                        selectedCountries={props.selectedCountries}
                        onCountriesChange={props.onCountriesChange}
                        similaritySelectivity={props.similaritySelectivity}
                        onSimilaritySelectivityChange={props.onSimilaritySelectivityChange}
                        countryDistributionMode={props.countryDistributionMode}
                        onCountryDistributionModeChange={props.onCountryDistributionModeChange}
                        excludeHomepages={props.excludeHomepages}
                        onExcludeHomepagesChange={props.onExcludeHomepagesChange}
                        onLoadUrls={props.onLoadUrls}
                        isLoadingUrls={props.isLoadingUrls}
                        isAdmin={props.isAdmin}
                        loadedCount={props.urlsText ? props.urlsText.split('\n').filter(l => l.trim()).length : 0}
                    />
                )}

                {props.inputMode === 'manual' && (
                    <ManualInput
                        urlsText={props.urlsText}
                        onUrlsTextChange={props.onUrlsTextChange}
                        isDragOver={props.isDragOver}
                        onDragOver={props.onDragOver}
                        onDrop={props.onDrop}
                        onFileUpload={props.onFileUpload}
                        uploadedFileName={props.uploadedFileName}
                    />
                )}

                {props.inputMode === 'classifications' && props.urlsText && (
                    <div>
                        <label className="block text-sm font-medium text-ink mb-2">
                            URLs to Analyze ({props.urlsText.split('\n').filter(l => l.trim()).length} loaded)
                        </label>
                        <textarea
                            value={props.urlsText}
                            readOnly
                            rows={10}
                            className="w-full px-3 py-2 border border-line rounded-md bg-canvas text-sm"
                        />
                    </div>
                )}

                <div className="flex gap-2">
                    <button
                        onClick={props.onAnalyze}
                        disabled={props.isAnalyzing || (props.inputMode === 'manual' && !props.urlsText.trim()) || !props.selectedPromptId}
                        className={`rounded-full bg-gradient-accent px-5 py-2 text-sm font-bold text-white focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                            props.isAnalyzing ? 'opacity-50' : 'hover:opacity-90'
                        }`}
                    >
                        {props.isAnalyzing ? 'Analyzing URLs...' : 'Analyze URLs'}
                    </button>
                    {props.isAnalyzing && props.streamingAbort && (
                        <button
                            onClick={props.onCancel}
                            className="rounded-full bg-risk-bg px-4 py-2 text-sm font-bold text-risk hover:opacity-80 focus:ring-2 focus:ring-risk focus:ring-offset-2"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ──────────────────── Classifications Controls ──────────────────── */

function ClassificationsControls(props: {
    databaseUrlCount: number; onDatabaseUrlCountChange: (v: number) => void;
    domainFilter: string; onDomainFilterChange: (v: string) => void;
    selectedCountries: string[]; onCountriesChange: (v: string[]) => void;
    similaritySelectivity: number; onSimilaritySelectivityChange: (v: number) => void;
    countryDistributionMode: 'uniform' | 'proportional';
    onCountryDistributionModeChange: (v: 'uniform' | 'proportional') => void;
    excludeHomepages: boolean; onExcludeHomepagesChange: (v: boolean) => void;
    onLoadUrls: () => void; isLoadingUrls: boolean; isAdmin: boolean;
    loadedCount: number;
}) {
    const maxUrls = props.isAdmin ? 10000 : 1000;
    const parsedDomains = parseDomainFilter(props.domainFilter);
    const overLimit = parsedDomains.length > MAX_DOMAIN_FILTER;
    return (
        <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
            <h3 className="mb-3 text-xs font-extrabold text-ink">Classification Database</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="flex flex-col gap-1">
                    <label className="pl-0.5 text-xs font-bold uppercase tracking-wide text-ink-muted">
                        Number of URLs (1-{maxUrls.toLocaleString()})
                    </label>
                    <input
                        type="number" min="1" max={maxUrls}
                        value={props.databaseUrlCount}
                        onChange={e => props.onDatabaseUrlCountChange(Math.min(Math.max(parseInt(e.target.value) || 1, 1), maxUrls))}
                        className="w-full rounded-full border border-line bg-transparent px-4 py-1.5 text-xs text-ink focus:border-accent focus:ring-accent"
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="pl-0.5 text-xs font-bold uppercase tracking-wide text-ink-muted">
                        Country Filter (optional)
                    </label>
                    <CountryMultiSelect
                        value={props.selectedCountries}
                        onChange={props.onCountriesChange}
                        placeholder="All countries"
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="pl-0.5 text-xs font-bold uppercase tracking-wide text-ink-muted">
                        Similarity Selectivity (%): {props.similaritySelectivity}
                    </label>
                    <div className="flex items-center gap-2 py-1.5">
                        <span className="text-xs text-ink-muted">all</span>
                        <input
                            type="range" min="0" max="100" step="5"
                            value={props.similaritySelectivity}
                            onChange={e => props.onSimilaritySelectivityChange(parseInt(e.target.value))}
                            className="h-[5px] w-full cursor-pointer appearance-none rounded-full bg-line [&::-moz-range-thumb]:h-[13px] [&::-moz-range-thumb]:w-[13px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[2.5px] [&::-moz-range-thumb]:border-accent [&::-moz-range-thumb]:bg-surface [&::-webkit-slider-thumb]:h-[13px] [&::-webkit-slider-thumb]:w-[13px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[2.5px] [&::-webkit-slider-thumb]:border-accent [&::-webkit-slider-thumb]:bg-surface"
                            style={{background: `linear-gradient(90deg, var(--accent) 0%, #5A9CFF ${props.similaritySelectivity}%, var(--line) ${props.similaritySelectivity}%, var(--line) 100%)`}}
                        />
                        <span className="text-xs text-ink-muted">top</span>
                    </div>
                </div>
                {/* Country Distribution (bd-g6m3q.3) — only meaningful with
                    multiple countries selected. */}
                <div className="flex flex-col gap-1">
                    <label className="pl-0.5 text-xs font-bold uppercase tracking-wide text-ink-muted">
                        Country Distribution
                    </label>
                    <div className="inline-flex w-max rounded-full bg-ghost-bg p-[3px]">
                        <Button
                            type="button"
                            variant="pill"
                            size="sm"
                            active={props.countryDistributionMode === 'proportional'}
                            onClick={() => props.onCountryDistributionModeChange('proportional')}
                        >
                            Proportional
                        </Button>
                        <Button
                            type="button"
                            variant="pill"
                            size="sm"
                            active={props.countryDistributionMode === 'uniform'}
                            onClick={() => props.onCountryDistributionModeChange('uniform')}
                        >
                            Uniform
                        </Button>
                    </div>
                    <p className="text-sm text-ink-muted">
                        {props.countryDistributionMode === 'proportional'
                            ? 'Distributes URLs based on available pool size per country'
                            : 'Distributes URLs evenly across countries'}
                    </p>
                </div>
                {/* Exclude Homepages (bd-deepcontext-vql07) — mirrors the
                    scheduled-jobs control, default checked. */}
                <div className="flex flex-col gap-1 justify-end">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={props.excludeHomepages}
                            onChange={e => props.onExcludeHomepagesChange(e.target.checked)}
                            className="peer sr-only"
                        />
                        <span
                            aria-hidden="true"
                            className="flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-[5px] border border-line bg-surface text-xs text-white peer-checked:border-transparent peer-checked:bg-gradient-accent"
                        >
                            ✓
                        </span>
                        <span className="text-sm font-semibold text-ink">Exclude Homepages</span>
                    </label>
                </div>
                <div className="flex flex-col gap-1 sm:col-span-3">
                    <label htmlFor="abDomainFilter" className="pl-0.5 text-xs font-bold uppercase tracking-wide text-ink-muted">
                        Domain Filter (optional, max {MAX_DOMAIN_FILTER})
                    </label>
                    <textarea
                        id="abDomainFilter"
                        value={props.domainFilter}
                        onChange={e => props.onDomainFilterChange(e.target.value)}
                        placeholder={"Enter domains (one per line or comma-separated)\ne.g. example.com, news.com"}
                        rows={3}
                        className={`w-full rounded-xl border bg-transparent px-3 py-2 text-xs text-ink focus:ring-accent ${
                            overLimit ? 'border-risk focus:border-risk' : 'border-line focus:border-accent'
                        }`}
                    />
                    {props.domainFilter.trim() && (
                        <p className={`text-sm ${overLimit ? 'text-risk' : 'text-ink-muted'}`}>
                            {parsedDomains.length}/{MAX_DOMAIN_FILTER} domain(s) specified
                            {overLimit && ' - exceeds limit!'}
                        </p>
                    )}
                </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
                <button
                    type="button"
                    onClick={props.onLoadUrls}
                    disabled={props.isLoadingUrls}
                    className="rounded-full bg-ghost-bg px-4 py-1.5 text-sm font-bold text-ink hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {props.isLoadingUrls ? 'Loading URLs...' : 'Preview URLs'}
                </button>
                {props.loadedCount > 0 && (
                    <span className="text-sm text-ink-muted">
                        <b className="font-bold text-safe">{props.loadedCount.toLocaleString('en-US')} URLs loaded</b> from classifications
                        {parsedDomains.length > 0 && ` · ${parsedDomains.length} domains filtered`}
                    </span>
                )}
            </div>
        </div>
    );
}

/* ──────────────────── Manual Input ──────────────────── */

function ManualInput({urlsText, onUrlsTextChange, isDragOver, onDragOver, onDrop, onFileUpload, uploadedFileName}: {
    urlsText: string; onUrlsTextChange: (v: string) => void;
    isDragOver: boolean; onDragOver: (v: boolean) => void;
    onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
    onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    uploadedFileName: string | null;
}) {
    return (
        <>
            <div
                className={`relative ${isDragOver ? 'ring-2 ring-accent ring-opacity-50' : ''}`}
                onDragOver={e => { e.preventDefault(); onDragOver(true); }}
                onDragLeave={() => onDragOver(false)}
                onDrop={onDrop}
            >
                <label className="block text-sm font-medium text-ink mb-2">
                    Enter URLs (one per line) or drag & drop a file
                </label>
                <textarea
                    rows={10}
                    className="block w-full px-3 py-2 border border-line rounded-md focus:ring-accent focus:border-accent"
                    placeholder={"https://example.com\nhttps://another-example.com\n..."}
                    value={urlsText}
                    onChange={e => {
                        const decoded = e.target.value.split('\n').map(url => {
                            const trimmed = url.trim();
                            if (!trimmed) return '';
                            try { return decodeURIComponent(trimmed); } catch { return trimmed; }
                        }).join('\n');
                        onUrlsTextChange(decoded);
                    }}
                />
                {isDragOver && (
                    <div className="absolute inset-0 bg-accent/10 bg-opacity-90 flex items-center justify-center rounded-md">
                        <p className="text-accent-active font-medium">Drop your file here</p>
                    </div>
                )}
            </div>
            <div className="flex items-center gap-4">
                <label className="flex items-center px-4 py-2 bg-canvas text-ink rounded-md hover:bg-ghost-bg cursor-pointer">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                    </svg>
                    Upload File
                    <input type="file" className="hidden" accept=".txt,.csv" onChange={onFileUpload} />
                </label>
                {uploadedFileName && <span className="text-sm text-ink-muted">Uploaded: {uploadedFileName}</span>}
                <span className="text-sm text-ink-muted">Supports .txt and .csv files</span>
            </div>
        </>
    );
}

/* ──────────────────── Progress, Warning, Error ──────────────────── */

function ProgressDisplay({progress, originalRequestedCount}: {
    progress: {current: number; total: number; currentChunk: number; totalChunks: number; status: string};
    originalRequestedCount: number | null;
}) {
    const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;
    return (
        <div data-testid="relevance-progress-panel" className="mt-6 rounded-2xl border-[1.5px] border-accent bg-surface p-4">
            <h3 className="mb-2 flex items-center gap-2 text-xs font-extrabold text-ink">
                <span className="h-3 w-3 rounded-full border-2 border-accent border-t-transparent animate-spin"/>
                Analysis Progress
            </h3>
            <div className="relative h-[9px] overflow-hidden rounded-full bg-line">
                <div
                    data-testid="progress-bar-fill"
                    className="absolute inset-y-0 left-0 rounded-full bg-gradient-accent transition-all duration-500"
                    style={{width: `${pct}%`}}
                >
                    <div
                        data-testid="progress-bar-shimmer"
                        className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/35 to-transparent"
                    />
                </div>
            </div>
            <div className="mt-1.5 flex text-sm font-semibold text-ink-muted">
                <span>{progress.status}</span>
                <span className="ml-auto">
                    <b className="text-ink">{progress.current.toLocaleString('en-US')}/{progress.total.toLocaleString('en-US')}</b> URLs
                    {originalRequestedCount && originalRequestedCount !== progress.total && (
                        <span> (of {originalRequestedCount.toLocaleString('en-US')} requested)</span>
                    )}
                    {' · '}<b className="text-ink">{pct}%</b> complete
                    {progress.totalChunks > 1 && <> · Chunk <b className="text-ink">{progress.currentChunk}/{progress.totalChunks}</b></>}
                </span>
            </div>
        </div>
    );
}

function WarningDisplay({message}: {message: string}) {
    return (
        <div className="bg-warn-bg border-l-4 border-warn p-4 mt-6">
            <div className="flex">
                <svg className="h-5 w-5 text-warn flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                <p className="ml-3 text-sm text-warn">{message}</p>
            </div>
        </div>
    );
}

function ErrorDisplay({message}: {message: string}) {
    return (
        <div className="bg-risk-bg border-l-4 border-risk p-4 mt-6">
            <div className="flex">
                <svg className="h-5 w-5 text-risk flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                </svg>
                <p className="ml-3 text-sm text-risk">{message}</p>
            </div>
        </div>
    );
}

/* ──────────────────── Summary ──────────────────── */

function SummarySection({result, excludedUrlsCount, isAdmin}: {
    result: BulkAnalysisResult; excludedUrlsCount: number | null; isAdmin: boolean;
}) {
    return (
        <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                <div className="rounded-2xl border border-line bg-surface p-3 shadow-sm">
                    <div className="text-xs font-semibold text-ink-muted">
                        Total URLs
                        {excludedUrlsCount && excludedUrlsCount > 0 && (
                            <span className="ml-1 text-warn">({excludedUrlsCount} excluded)</span>
                        )}
                    </div>
                    <div className="mt-0.5 text-base font-extrabold text-ink">{result.summary.totalUrls}</div>
                </div>
                <div className="rounded-2xl border border-line bg-surface p-3 shadow-sm">
                    <div className="text-xs font-semibold text-ink-muted">Match</div>
                    <div className="mt-0.5 text-base font-extrabold text-safe">{result.summary.matchedUrls || 0}</div>
                </div>
                <div className="rounded-2xl border border-line bg-surface p-3 shadow-sm">
                    <div className="text-xs font-semibold text-ink-muted">No Match</div>
                    <div className="mt-0.5 text-base font-extrabold text-risk">{result.summary.unmatchedUrls || 0}</div>
                </div>
                {(result.summary.failedUrls && result.summary.failedUrls > 0) && (
                    <div className="rounded-2xl border border-line bg-surface p-3 shadow-sm">
                        <div className="text-xs font-semibold text-ink-muted">Failed</div>
                        <div className="mt-0.5 text-base font-extrabold text-risk">{result.summary.failedUrls}</div>
                    </div>
                )}
                <div className="rounded-2xl border border-line bg-surface p-3 shadow-sm">
                    <div className="text-xs font-semibold text-ink-muted">Avg Confidence</div>
                    <div className="mt-0.5 text-base font-extrabold text-ink">{Math.round((result.summary.averageConfidence || 0) * 100)}%</div>
                </div>
            </div>
            {result.summary.totalInputTokens && result.summary.totalOutputTokens && isAdmin && (
                <div className="flex gap-4 px-1 text-sm font-semibold text-ink-muted">
                    <span>Input tokens <b className="text-ink">{result.summary.totalInputTokens.toLocaleString()}</b></span>
                    <span>Output tokens <b className="text-ink">{result.summary.totalOutputTokens.toLocaleString()}</b></span>
                    <span>Total cost <b className="text-ink">${result.totalCost.toFixed(2)}</b></span>
                </div>
            )}
        </div>
    );
}

/* ──────────────────── Results ──────────────────── */

function ResultsSection({displayResults, showExportMenu, onToggleExportMenu, result, onFeedbackSubmitted}: {
    displayResults: AnalysisResult[];
    showExportMenu: boolean;
    onToggleExportMenu: () => void;
    result: BulkAnalysisResult | null;
    onFeedbackSubmitted: (id: string, agree: boolean) => void;
}) {
    return (
        <div>
            <div className="flex items-center">
                <h3 className="text-sm font-extrabold text-ink">Analysis Results</h3>
                <div className="relative ml-auto">
                    <Button variant="outline" onClick={onToggleExportMenu}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"/>
                        </svg>
                        Export Results
                    </Button>
                    {showExportMenu && (
                        <div className="absolute right-0 mt-2 w-48 rounded-2xl border border-line bg-surface shadow-lg z-10">
                            <button onClick={() => { exportAllResults(displayResults, 'csv', result || undefined, false); onToggleExportMenu(); }}
                                    className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-ghost-bg">Export as CSV</button>
                            <button onClick={() => { exportAllResults(displayResults, 'excel', result || undefined, false); onToggleExportMenu(); }}
                                    className="block w-full text-left px-4 py-2 text-sm text-ink hover:bg-ghost-bg">Export as Excel</button>
                        </div>
                    )}
                </div>
            </div>
            {/* One shared paginated table for both Relevance Engine modes
                (deepcontext-8snj) — no more unbounded card stack. */}
            <div className="mt-4">
                <AnalysisResultsTable
                    results={displayResults}
                    onFeedbackSubmitted={onFeedbackSubmitted}
                />
            </div>
        </div>
    );
}

export default RelevanceEngineAB;
